import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const LegalShell = ({ title, chapters, testid }) => (
  <div className="min-h-screen bg-[#f7f5f0] text-[#1a1918]" data-testid={testid}>
    <Helmet>
      <title>{`${title} — ProSecours`}</title>
      <meta name="robots" content="noindex, follow" />
    </Helmet>
    <header className="border-b border-[#1a1918]/15">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="font-serif-ed text-xl md:text-2xl">
          ProSecours
        </Link>
        <Link
          to="/"
          data-testid="legal-back-home"
          className="overline text-[#1a1918]/70 hover:text-[#1a1918]"
        >
          ← Retour
        </Link>
      </div>
    </header>
    <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-28 grid grid-cols-12 gap-6 md:gap-10">
      <div className="col-span-12 md:col-span-4">
        <div className="md:sticky md:top-28">
          <span className="overline text-[#1a1918]/60">§ Cadre juridique</span>
          <h1 className="mt-6 font-serif-ed text-5xl md:text-6xl leading-[1] tracking-tight">
            {title}
          </h1>
        </div>
      </div>
      <div className="col-span-12 md:col-span-7 md:col-start-6 space-y-14">
        {chapters.map((c, i) => (
          <section key={i} className="border-t border-[#1a1918]/25 pt-8">
            <div className="chapter-num text-3xl text-[#1a1918]/60">
              0{i + 1}
            </div>
            <h2 className="mt-2 font-serif-ed text-3xl md:text-4xl italic">
              {c.title}
            </h2>
            <div className="mt-5 space-y-4 text-base md:text-lg leading-relaxed text-[#1a1918]/80">
              {c.paragraphs.map((p, j) => (
                <p key={j}>{p}</p>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
    <footer className="border-t border-[#1a1918]/15 py-8">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 overline text-[#1a1918]/50">
        © MMXXV ProSecours
      </div>
    </footer>
  </div>
);

export function MentionsLegales() {
  return (
    <LegalShell
      title="Mentions légales"
      testid="mentions-legales-page"
      chapters={[
        {
          title: "Éditeur du site",
          paragraphs: [
            "Le présent site est édité par ProSecours, société de formalités juridiques basée à Paris.",
            "Responsable de la publication : Gérard Adade.",
            "Téléphone : 07 44 88 33 81. Pour tout contact écrit, vous pouvez utiliser le formulaire présent sur le site.",
            "Zone d'intervention : Paris & Île-de-France, France entière sur demande.",
          ],
        },
        {
          title: "Hébergement",
          paragraphs: [
            "Le site est hébergé sur une infrastructure cloud sécurisée mise à disposition via la plateforme Emergent (Emergent Labs).",
            "L'hébergeur peut être contacté pour toute demande liée à la disponibilité technique du site.",
          ],
        },
        {
          title: "Propriété intellectuelle",
          paragraphs: [
            "L'ensemble des textes, visuels, marques, logos et éléments graphiques présents sur le site sont la propriété exclusive d'ProSecours ou de leurs auteurs respectifs.",
            "Toute reproduction, représentation ou diffusion, totale ou partielle, sans autorisation écrite préalable, est strictement interdite et constitue une contrefaçon sanctionnée par les articles L.335-2 et suivants du Code de la propriété intellectuelle.",
          ],
        },
        {
          title: "Limitation de responsabilité",
          paragraphs: [
            "Les informations diffusées sur le site sont fournies à titre indicatif. Elles ne constituent en aucun cas une étude juridique, comptable ou financière personnalisée.",
            "Vous choisissez la formalité ou la procédure à engager. ProSecours prépare les actes, réunit les pièces et organise les dépôts auprès des services compétents.",
          ],
        },
        {
          title: "Droit applicable",
          paragraphs: [
            "Le présent site et son contenu sont soumis au droit français. Tout litige relatif à leur utilisation relève de la compétence exclusive des tribunaux français.",
          ],
        },
      ]}
    />
  );
}

export function Confidentialite() {
  return (
    <LegalShell
      title="Politique de confidentialité"
      testid="confidentialite-page"
      chapters={[
        {
          title: "Responsable de traitement",
          paragraphs: [
            "ProSecours est responsable du traitement des données à caractère personnel collectées via le présent site, conformément au Règlement Général sur la Protection des Données (RGPD, UE 2016/679) et à la loi Informatique et Libertés modifiée.",
            "Pour toute question, vous pouvez nous contacter par téléphone au 07 44 88 33 81 ou via le formulaire présent sur le site.",
          ],
        },
        {
          title: "Données collectées",
          paragraphs: [
            "Nous collectons uniquement les données que vous nous transmettez volontairement via le formulaire d'audit ou de contact : nom, société, téléphone, adresse électronique, message et, le cas échéant, date de rendez-vous souhaitée.",
            "Aucune donnée sensible au sens de l'article 9 du RGPD n'est demandée ni traitée.",
          ],
        },
        {
          title: "Finalités du traitement",
          paragraphs: [
            "Les données sont exclusivement utilisées pour répondre à votre demande, organiser un premier échange et, le cas échéant, formaliser une mission de formalités juridiques.",
            "Aucune donnée n'est cédée, revendue ou transmise à des tiers commerciaux. Certains prestataires techniques (hébergeur, service de messagerie) peuvent accéder aux données strictement dans le cadre de l'exécution du service.",
          ],
        },
        {
          title: "Durée de conservation",
          paragraphs: [
            "Les demandes reçues sont conservées pendant une durée maximale de 36 mois à compter du dernier échange, puis supprimées ou anonymisées.",
            "Les données relatives à une mission signée sont conservées conformément aux obligations légales et comptables applicables.",
          ],
        },
        {
          title: "Vos droits",
          paragraphs: [
            "Vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, de portabilité et d'opposition sur vos données personnelles.",
            "Vous pouvez exercer ces droits à tout moment en nous contactant par téléphone au 07 44 88 33 81 ou via le formulaire du site. Vous disposez également du droit d'introduire une réclamation auprès de la CNIL (www.cnil.fr).",
          ],
        },
        {
          title: "Cookies",
          paragraphs: [
            "Le site n'utilise pas de cookie publicitaire ou de traçage tiers. Seules des mesures techniques nécessaires au bon fonctionnement du site peuvent être mises en place.",
          ],
        },
      ]}
    />
  );
}
